import React, { useState, useEffect, useMemo } from 'react';
import Sidebar from './Sidebar';
import ProfileMenu from './ProfileMenu';
import MobileHamburger from './MobileHamburger';
import MobileMenu from './MobileMenu';
import { supabase } from './supabaseClient';
import { GoogleMap, useJsApiLoader, Marker, InfoWindow, OverlayView } from '@react-google-maps/api';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaChartSimple, FaCheck, FaClock, FaFileCircleExclamation, FaHouse, FaHouseUser, FaTent, FaUserGroup, FaBuilding, FaMapPin } from 'react-icons/fa6'; 
import { TbChartHistogram, TbMapPinExclamation, TbHomePlus, TbFilePencil, TbUserQuestion, TbUsers, TbTent, TbCheck, TbClock, TbBell, TbShield, TbShieldCheck, TbUser, TbSettings, TbLogout } from 'react-icons/tb';
import { LuMegaphone } from 'react-icons/lu';
import { FaRegIdCard } from 'react-icons/fa';
import { useAuth } from './AuthContext';
import Preloader from './Preloader';
const libraries = ['places', 'marker'];

function Dashboard() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [mapCenter, setMapCenter] = useState({ lat: -6.2088, lng: 106.8456 }); // Default center (e.g., Jakarta)
  const [shelters, setShelters] = useState([]);
  const [selectedShelter, setSelectedShelter] = useState(null);

  // Statistics states
  const [totalReports, setTotalReports] = useState(0);
  const [reportsActive, setReportsActive] = useState(0);
  const [pendingTemporaryShelters, setPendingTemporaryShelters] = useState(0);
  const [activeTemporaryShelters, setActiveTemporaryShelters] = useState(0);
  const [occupiedEvacuationCenters, setOccupiedEvacuationCenters] = useState(0);
  const [numberOfEvacuationCenters, setNumberOfEvacuationCenters] = useState(0);
  const [availableEvacuationCenters, setAvailableEvacuationCenters] = useState(0);
  const [numberOfEvacuees, setNumberOfEvacuees] = useState(0);
  const [recentAnnouncements, setRecentAnnouncements] = useState([]);
  const [approvedTempShelters, setApprovedTempShelters] = useState([]);
  const [shelterFilter, setShelterFilter] = useState('all'); 
  const [sidebarNarrow, setSidebarNarrow] = useState(false);
  const [infographicImages, setInfographicImages] = useState([]);
  const [loadingInfographics, setLoadingInfographics] = useState(false);
  const [expandedInfographic, setExpandedInfographic] = useState(null);
  const [uploadingInfographic, setUploadingInfographic] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [infographicTitle, setInfographicTitle] = useState(''); 
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const navigate = useNavigate();
  const location = useLocation();
  const { signOut } = useAuth();

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: 'AIzaSyAugo7vMiL-tP9dyz6mlCvcZqcovWJl1eA', 
    libraries
  });


  const mapContainerStyle = {
    width: '100%',
    height: '400px', 
    borderRadius: '12px',
    overflow: 'hidden'
  };

  const mapOptions = useMemo(() => ({
    disableDefaultUI: true,
    zoomControl: true,
    mapId: 'YOUR_MAP_ID', 
    styles: [
      {
        featureType: 'poi',
        elementType: 'labels',
        stylers: [{ visibility: 'off' }]
      }
    ],
    gestureHandling: 'cooperative',
    zoomControlOptions: {
      position: window.google?.maps?.ControlPosition?.RIGHT_TOP || 1
    }
  }), []);

  // Function to get shelter marker icon
  const getShelterMarkerIcon = (shelterType) => {
    if (shelterType === 'evacuation_center') {
      return <FaBuilding />;
    } else if (shelterType === 'temporary_shelter') {
      return <FaTent />;
    }
    return <FaMapPin />;
  };

  useEffect(() => {
      document.title = "Dashboard";
    }, []);

  // Check screen size and close mobile menu if not mobile
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      
      // Close mobile menu if screen becomes larger than mobile
      if (!mobile && showMobileMenu) {
        setShowMobileMenu(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [showMobileMenu]);

  useEffect(() => {
    fetchAllData();

    const incidentReportsChannel = supabase
      .channel('public:incident_reports')
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'incident_reports' },
        (payload) => {
          console.log('Incident reports change received!', payload);
          fetchAllData();
        }
      )
      .subscribe();

    const tempShelterChannel = supabase
      .channel('public:temp_shelter_dashboard') // Using a different channel name
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'temp_shelter' },
        (payload) => {
          console.log('Temporary shelter change received!', payload);
          fetchApprovedTempShelters();
        }
      )
      .subscribe();

    const evacuationSheltersChannel = supabase
      .channel('public:evacuation_shelters_dashboard') // Using a different channel name
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'evacuation_shelters' },
        (payload) => {
          console.log('Evacuation shelters change received!', payload);
          fetchAllData();
        }
      )
      .subscribe();

    const announcementsChannel = supabase
      .channel('public:announcements_dashboard') // Using a different channel name
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'announcements' },
        (payload) => {
          console.log('Announcements change received!', payload);
          fetchAllData();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(incidentReportsChannel);
      supabase.removeChannel(tempShelterChannel);
      supabase.removeChannel(evacuationSheltersChannel);
      supabase.removeChannel(announcementsChannel);
    };
  }, []);

  async function fetchAllData() {
    setLoading(true);
    setError('');

    try {
      // Fetch Incident Reports Data
      const { data: incidentsData, error: incidentsError } = await supabase
        .from('incident_reports')
        .select('*');

      if (incidentsError) throw incidentsError;
      setTotalReports(incidentsData.length);
      setReportsActive(incidentsData.filter(i => i.status === 'active').length);

      // Fetch Pending Temporary Shelters
      const { data: pendingTempShelterData, error: pendingTempShelterError } = await supabase
        .from('temp_shelter')
        .select('id')
        .eq('status', 'pending');

      if (pendingTempShelterError) throw pendingTempShelterError;
      setPendingTemporaryShelters(pendingTempShelterData.length);

      // Fetch Shelter Data for Map and Statistics
      let evacuationCenters = [];
      let temporaryShelters = [];

      const { data: evacuationData, error: evacuationError } = await supabase
        .from('evacuation_shelters')
        .select('*');
      if (evacuationError) throw evacuationError;
      evacuationCenters = (evacuationData || []).map(shelter => ({
        ...shelter,
        shelter_type: 'evacuation_center',
        name: shelter.shelter_name,
        latitude: shelter.latitude,
        longitude: shelter.longitude,
      }));
      console.log('Evacuation centers mapped:', evacuationCenters);

      const { data: temporaryData, error: temporaryError } = await supabase
        .from('temp_shelter')
        .select('*')
        .eq('status', 'approved');
      if (temporaryError) throw temporaryError;
      temporaryShelters = (temporaryData || []).map(shelter => ({
        ...shelter,
        shelter_type: 'temporary_shelter',
        name: shelter.house_name,
        latitude: shelter.latitude,
        longitude: shelter.longitude,
      }));
      console.log('Temporary shelters mapped:', temporaryShelters);

      // Filter out archived shelters
      const allShelters = [...evacuationCenters, ...temporaryShelters].filter(shelter =>
        shelter.latitude && typeof shelter.latitude === 'number' &&
        shelter.longitude && typeof shelter.longitude === 'number' &&
        shelter.status !== 'archived'
      );
      console.log('All shelters for map:', allShelters);
      setShelters(allShelters);

      // Calculate shelter statistics
      let totalEvacCenters = 0;
      let currentEvacuees = 0;
      let totalCapacityEvacCenters = 0;
      let occupiedEvacCentersCount = 0;

      // First count evacuation center occupants
      evacuationCenters.forEach(shelter => {
        totalEvacCenters++;
        currentEvacuees += (shelter.accepted_occupants || 0);
        totalCapacityEvacCenters += (shelter.capacity || 0);
        if ((shelter.accepted_occupants || 0) >= (shelter.capacity || 0)) {
          occupiedEvacCentersCount++;
        }
      });

      // Then add temporary shelter occupants
      temporaryShelters.forEach(shelter => {
        currentEvacuees += (shelter.members || 0) + (shelter.accepted_occupants || 0);
      });

      setNumberOfEvacuationCenters(totalEvacCenters);
      setNumberOfEvacuees(currentEvacuees);
      setOccupiedEvacuationCenters(occupiedEvacCentersCount);
      setAvailableEvacuationCenters(totalCapacityEvacCenters - currentEvacuees);
      setActiveTemporaryShelters(temporaryShelters.length);

      if (allShelters.length > 0) {
        setMapCenter({ 
          lat: allShelters[0].latitude,
          lng: allShelters[0].longitude
        });
      }

      // Fetch Latest Announcement
      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

      const { data: announcementData, error: announcementError } = await supabase
        .from('announcements')
        .select('content, created_at, title')
        .gte('created_at', oneWeekAgo.toISOString())
        .order('created_at', { ascending: false })
        .limit(3); // Limit to 3 announcements
      
      if (announcementError) {
        console.error('Error fetching announcements:', announcementError);
        setRecentAnnouncements([]);
      } else if (announcementData) {
        setRecentAnnouncements(announcementData);
      }

    } catch (err) {
      console.error('Error fetching all data:', err);
      setError('Failed to load dashboard data.');
    } finally {
      setLoading(false);
    }
  }

  async function fetchApprovedTempShelters() {
    try {
      const { data, error } = await supabase
        .from('temp_shelter')
        .select('*')
        .eq('status', 'approved'); // Only get approved temporary shelters

      if (error) {
        console.error('Error fetching approved temporary shelters:', error);
        setApprovedTempShelters([]);
      } else {
        setApprovedTempShelters(data || []);
      }
    } catch (error) {
      console.error('Error in fetchApprovedTempShelters:', error);
      setApprovedTempShelters([]);
    }
  }

  // Filter shelters based on selected filter
  const filteredShelters = shelters.filter(shelter => {
    if (shelterFilter === 'all') return true;
    if (shelterFilter === 'evacuation') return shelter.shelter_type === 'evacuation_center';
    if (shelterFilter === 'temporary') return shelter.shelter_type === 'temporary_shelter';
    return true;
  });

// Function to fetch infographic images from database
// Update your fetch function to log more details
const fetchInfographicImages = async () => {
  setLoadingInfographics(true);
  try {
    console.log('Fetching infographics from database...');
    
    const { data: infographics, error } = await supabase
      .from('offline')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;

    console.log('Raw database records:', infographics);

    if (!infographics || infographics.length === 0) {
      setInfographicImages([]);
      return;
    }

    // QUICK FIX: Just pass the original URLs without modifying database
    const images = infographics.map(offline => ({
      id: offline.id,
      title: offline.title,
      url: offline.image_url, // Keep original URL from database
      createdAt: offline.created_at,
    }));

    console.log('Mapped images:', images);
    setInfographicImages(images);
  } catch (error) {
    console.error('Error fetching infographics:', error);
    setInfographicImages([]);
  } finally {
    setLoadingInfographics(false);
  }
};

// Fetch infographic images on component mount
useEffect(() => {
  fetchInfographicImages();
}, []);

// Function to close upload modal and reset state
const closeUploadModal = () => {
  setShowUploadModal(false);
  setSelectedFile(null);
  setInfographicTitle('');
  // Clear the file input
  const fileInput = document.querySelector('input[type="file"]');
  if (fileInput) fileInput.value = '';
};

// Function to handle file selection
const handleFileSelect = (event) => {
  const file = event.target.files[0];
  if (file) {
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      alert('Please select a valid image file (JPEG, PNG, or GIF)');
      return;
    }
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB');
      return;
    }
    setSelectedFile(file);
  }
};

// Function to upload infographic - FIXED VERSION
const uploadInfographic = async () => {
  if (!selectedFile) {
    alert('Please select a file to upload');
    return;
  }

  if (!infographicTitle.trim()) {
    alert('Please enter a title for the infographic');
    return;
  }

  setUploadingInfographic(true);
  try {
    console.log('Uploading file:', selectedFile.name, 'Size:', selectedFile.size, 'Title:', infographicTitle);
    
    // Generate unique filename
    const fileName = `${Date.now()}_${selectedFile.name}`;
    console.log('Generated filename:', fileName);
    
    // Upload file to storage - FIXED: consistent bucket name
    const { data: uploadData, error: uploadError } = await supabase.storage
      .from('offline-images') // Using offline-images bucket consistently
      .upload(fileName, selectedFile, {
        cacheControl: '3600',
        upsert: false,
        contentType: selectedFile.type
      });

    if (uploadError) {
      console.error('Upload error:', uploadError);
      if (uploadError.message?.includes('permission') || uploadError.message?.includes('policy')) {
        alert('Permission denied. Please contact administrator to set up storage policies for infographics.');
      } else {
        alert(`Upload failed: ${uploadError.message}`);
      }
      throw uploadError;
    }

    console.log('File upload successful:', uploadData);

    // Get the public URL - FIXED: consistent bucket name
    const { data: { publicUrl } } = supabase.storage
      .from('offline-images') // Using offline-images bucket consistently
      .getPublicUrl(fileName);

    console.log('Public URL:', publicUrl);

    // Save infographic metadata to database - FIXED: using correct table and columns
    const { data: dbData, error: dbError } = await supabase
      .from('offline') // Using correct table name
      .insert({
        title: infographicTitle.trim(),
        image_url: publicUrl // Using correct column name
        // created_at will be auto-generated by database
        // Remove fields that don't exist: file_name, file_size, content_type, uploaded_by, status
      })
      .select()
      .single();

    if (dbError) {
      console.error('Database error:', dbError);
      // Try to clean up the uploaded file if database save failed - FIXED: consistent bucket name
      await supabase.storage.from('offline-images').remove([fileName]);
      alert(`Failed to save infographic info: ${dbError.message}`);
      throw dbError;
    }

    console.log('Database save successful:', dbData);

    // Refresh the infographics list
    await fetchInfographicImages();
    closeUploadModal();
    alert('Infographic uploaded successfully!');
  } catch (error) {
    console.error('Error uploading infographic:', error);
    if (!error.message?.includes('permission') && !error.message?.includes('policy')) {
      alert('Error uploading infographic. Please try again.');
    }
  } finally {
    setUploadingInfographic(false);
  }
};

// Function to delete infographic - FIXED VERSION
const deleteInfographic = async (infographicId, fileName) => {
  if (!window.confirm('Are you sure you want to delete this infographic?')) {
    return;
  }

  try {
    console.log('Deleting infographic:', infographicId, fileName);

    // Delete from database - FIXED: using correct table name and hard delete
    const { error: dbError } = await supabase
      .from('offline') // Using correct table name
      .delete() // Using hard delete since no status column for soft delete
      .eq('id', infographicId);

    if (dbError) {
      console.error('Database error:', dbError);
      throw dbError;
    }

    // Then delete the actual file from storage - FIXED: consistent bucket name
    const { error: storageError } = await supabase.storage
      .from('offline-images') // Using offline-images bucket consistently
      .remove([fileName]);

    if (storageError) {
      console.error('Storage error:', storageError);
      // Even if storage deletion fails, we can continue since DB is updated
      console.log('File deletion from storage failed, but database record deleted');
    }

    // Refresh the infographics list
    await fetchInfographicImages();
    alert('Infographic deleted successfully!');
  } catch (error) {
    console.error('Error deleting infographic:', error);
    alert('Error deleting infographic. Please try again.');
  }
};

  if (loading) return <Preloader text="Loading Dashboard..." />;

  if (error) return <div style={{ color: 'red' }}>{error}</div>;

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f4f8f8', width: '100%', overflow: 'hidden' }}>
      <Sidebar onNarrowChange={setSidebarNarrow} />
              <div
          className="dashboard-main-content"
          style={{
            flex: 1,
            padding: 32,
            marginLeft: sidebarNarrow ? 0 : 0, 
            transition: 'margin-left 0.2s',
            maxWidth: '100%',
            overflow: 'hidden'
          }}
        >
        <div className="header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
            {/* Mobile Hamburger Menu */}
            {isMobile && (
              <MobileHamburger
                isOpen={showMobileMenu}
                onToggle={() => setShowMobileMenu(prev => !prev)}
              />
            )}
            <div className="title-text">Dashboard</div>
          </div>
          <ProfileMenu />
        </div>

        {/* Mobile Dropdown Menu */}
        <MobileMenu 
          isOpen={showMobileMenu} 
          onClose={() => setShowMobileMenu(false)} 
        />

        {/* Main Content Grid */}
        <div className="dashboard-grid" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 24, marginBottom: 24, maxWidth: '100%', overflow: 'hidden' }}>

          {/* Left Column: Map and Latest Announcements */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24, maxWidth: '100%', overflow: 'hidden' }}>
            {/* Map Section */}
            <div className="map-container" style={{ 
              background: '#fff', 
              borderRadius: 12, 
              padding: 24, 
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)', 
              overflow: 'hidden',
              width: '100%',
              boxSizing: 'border-box'
            }}>

              <div style={{ position: 'relative', overflow: 'hidden', width: '100%' }}>
                {isLoaded ? (
                  <GoogleMap
                    mapContainerStyle={mapContainerStyle}
                    center={mapCenter}
                    zoom={12}
                    options={mapOptions}
                  >
                    {filteredShelters.map((shelter) => (
                      <OverlayView
                        key={shelter.id}
                        position={{ lat: shelter.latitude, lng: shelter.longitude }}
                        mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                        getPixelPositionOffset={(width, height) => ({ x: -(width / 2), y: -height })}
                      >
                        <div
                          onClick={() => setSelectedShelter(shelter)}
                          style={{
                            background: shelter.shelter_type === 'evacuation_center' ? '#1976d2' : '#ff9800',
                            color: 'white',
                            padding: '8px 12px',
                            borderRadius: '20px',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          <div style={{ marginRight: '8px', display: 'flex', alignItems: 'center', fontSize: '18px' }}>
                            {getShelterMarkerIcon(shelter.shelter_type)}
                          </div>
                          <span>{shelter.name || (shelter.shelter_type === 'evacuation_center' ? shelter.shelter_name : shelter.house_name) || 'Unknown Shelter'}</span>
                        </div>
                      </OverlayView>
                    ))}

                    {selectedShelter && (
                      <InfoWindow
                        position={{
                          lat: selectedShelter.latitude,
                          lng: selectedShelter.longitude,
                        }}
                        onCloseClick={() => setSelectedShelter(null)}
                      >
                        <div style={{ padding: '10px' }}>
                          {/* Image on top */}
                          {(() => {
                            let img = null;
                            if (selectedShelter.shelter_type === 'evacuation_center') {
                              img = selectedShelter.shelter_image1 || selectedShelter.shelter_image2 || selectedShelter.shelter_image3 || selectedShelter.shelter_image4;
                            } else if (selectedShelter.shelter_type === 'temporary_shelter') {
                              img = selectedShelter.house_image1 || selectedShelter.house_image2 || selectedShelter.house_image3 || selectedShelter.house_image4;
                            }
                            return img ? (
                              <img src={img} alt={selectedShelter.name} style={{ width: '100px', height: 'auto', marginBottom: '10px', borderRadius: 8, display: 'block', marginLeft: 'auto', marginRight: 'auto' }} />
                            ) : null;
                          })()}
                          <h3 style={{ marginBottom: '5px', marginTop: 0 }}>{selectedShelter.name}</h3>
                          <p><strong>Status:</strong> {selectedShelter.status ? selectedShelter.status.charAt(0).toUpperCase() + selectedShelter.status.slice(1) : '-'}</p>
                          <p><strong>Total Occupants:</strong> {selectedShelter.shelter_type === 'temporary_shelter' ? ((selectedShelter.members || 0) + (selectedShelter.accepted_occupants || 0)) : (selectedShelter.accepted_occupants || 0)}</p>
                          <p><strong>Remaining Capacity:</strong> {selectedShelter.capacity - (selectedShelter.shelter_type === 'temporary_shelter' ? ((selectedShelter.members || 0) + selectedShelter.accepted_occupants || 0) : (selectedShelter.accepted_occupants || 0))}</p>
                        </div>
                      </InfoWindow>
                    )}
                  </GoogleMap>
                ) : (
                  <div style={{ height: '400px', display: 'flex', justifyContent: 'center', alignItems: 'center', background: '#e0e0e0', borderRadius: 12 }}>
                    <Preloader text="Loading Maps..." />
                  </div>
                )}
                {/* Filter Dropdown */}
                <div style={{
                  position: 'absolute',
                  top: 20,
                  right: 20,
                  background: 'rgba(255,255,255,0.9)',
                  padding: 12,
                  borderRadius: 8,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  zIndex: 101
                }}>
                  <select 
                    value={shelterFilter}
                    onChange={(e) => setShelterFilter(e.target.value)}
                    style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #ccc', fontWeight: 'bold' }}
                  >
                    <option value="all">All Shelters</option>
                    <option value="evacuation">Evacuation Centers</option>
                    <option value="temporary">Temporary Shelters</option>
                  </select>
                </div>
                {/* Legend */}
                <div style={{ 
                  position: 'absolute', 
                  top: 20, 
                  left: 20, 
                  background: 'rgba(255,255,255,0.9)', 
                  padding: 12, 
                  borderRadius: 8, 
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)', 
                  zIndex: 100
                }}>
                  <div style={{ fontWeight: 'bold', marginBottom: 8 }}>Legend:</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}><FaBuilding style={{color: '#1976d2'}}/>Evacuation Center</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><FaTent style={{color: '#ff9800'}} />Temporary Shelter</div>
                </div>
              </div>
            </div>

            {/* Latest Announcements Card */}
            <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
              <div style={{ fontSize: 24, fontWeight: 'bold', marginBottom: 16 }}>Recent Announcements</div>
              {
                recentAnnouncements.length > 0 ? (
                  <ul style={{ listStyle: 'none', padding: 0 }}>
                    {recentAnnouncements.map((announcement, index) => (
                      <li 
                        key={index} 
                        style={{ marginBottom: 12, paddingBottom: 12, borderBottom: index < recentAnnouncements.length - 1 ? '1px solid #eee' : 'none', cursor: 'pointer' }}
                        onClick={() => navigate('/announcements')}
                      >
                        <div style={{ fontWeight: 'bold', fontSize: 16, marginBottom: 4 }}>{announcement.title}</div>
                        <div style={{ fontSize: 14, color: '#555' }}>{announcement.content}</div>
                        <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>{new Date(announcement.created_at).toLocaleDateString()}</div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div style={{ color: '#888', fontStyle: 'italic' }}>No new announcements in the last week.</div>
                )
              }
            </div>
            {/* Safety Tips / Infographics Card */}
            <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.05)', marginTop: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div style={{ fontSize: 24, fontWeight: 'bold' }}>Safety Tips & Infographics</div>
                <button
                  onClick={() => setShowUploadModal(true)}
                  style={{
                    background: '#3f51b5',
                    color: 'white',
                    border: 'none',
                    borderRadius: 8,
                    padding: '8px 16px',
                    cursor: 'pointer',
                    fontSize: 14,
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8
                  }}
                >
                  <span>+</span> Upload New
                </button>
              </div>
              {loadingInfographics ? (
                <div>Loading infographics...</div>
              ) : infographicImages.length > 0 ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                  {infographicImages.map(img => (
                    <div key={img.id} style={{ width: 140, textAlign: 'center', position: 'relative' }}>
                      <img
  src={img.url}
  alt={img.title}
  style={{ width: 120, height: 100, objectFit: 'cover', borderRadius: 8, boxShadow: '0 1px 4px #0001', cursor: 'pointer' }}
  loading="lazy"
  onClick={() => setExpandedInfographic(img)}
  onError={(e) => {
    console.error('Failed to load image:', img.url);
    // If it's an old signed URL, try to convert it to public URL format
    if (img.url.includes('/render/image/') || img.url.includes('?token=')) {
      const urlParts = img.url.split('/');
      const fileName = urlParts[urlParts.length - 1].split('?')[0];
      const publicUrl = `https://smxwiftwgzzkzacsyedo.supabase.co/storage/v1/object/public/offline-images/${fileName}`;
      console.log('Trying fallback URL:', publicUrl);
      e.target.src = publicUrl;
    }
  }}
  onLoad={() => console.log('Image loaded successfully:', img.title)}
/>
<button
  onClick={(e) => {
    e.stopPropagation();
    // Extract filename from the URL instead of using img.name
    const fileName = img.url.split('/').pop();
    deleteInfographic(img.id, fileName);
  }}
  style={{
    position: 'absolute',
    top: -8,
    right: -8,
    background: '#ff4444',
    color: 'white',
    border: 'none',
    borderRadius: '50%',
    width: 24,
    height: 24,
    cursor: 'pointer',
    fontSize: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }}
  title="Delete infographic"
>
  ×
</button>
                      <div style={{ 
                        fontSize: 11, 
                        marginTop: 4, 
                        fontWeight: '500',
                        color: '#333',
                        lineHeight: '1.3',
                        wordBreak: 'break-word',
                        maxHeight: '2.6em',
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical'
                      }}
                      title={img.title}
                      >
                        {img.title}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ 
                  color: '#888', 
                  fontStyle: 'italic', 
                  textAlign: 'center', 
                  padding: '40px 20px',
                  border: '2px dashed #ddd',
                  borderRadius: 8
                }}>
                  <div style={{ fontSize: 16, marginBottom: 8 }}>No safety tips or infographics available</div>
                  <div style={{ fontSize: 14 }}>Upload your first infographic using the "Upload New" button above.</div>
                </div>
              )}
              {expandedInfographic && (
                <div style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  width: '100vw',
                  height: '100vh',
                  background: 'rgba(0,0,0,0.7)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 9999
                }}
                  onClick={() => setExpandedInfographic(null)}
                >
                  <div style={{ position: 'relative', background: '#fff', borderRadius: 12, padding: 16, boxShadow: '0 4px 24px #0005', maxWidth: '90vw', maxHeight: '90vh' }} onClick={e => e.stopPropagation()}>
                    <button onClick={() => setExpandedInfographic(null)} style={{ position: 'absolute', top: 8, right: 8, background: 'transparent', border: 'none', fontSize: 28, cursor: 'pointer', color: '#333' }}>&times;</button>
                    <img src={expandedInfographic.url} alt={expandedInfographic.title} style={{ maxWidth: '80vw', maxHeight: '80vh', display: 'block', margin: '0 auto', borderRadius: 8 }} />
                    <div style={{ textAlign: 'center', marginTop: 12, padding: '0 16px' }}>
                      <h3 style={{ margin: '0 0 8px 0', fontWeight: 'bold', fontSize: 18, color: '#333' }}>
                        {expandedInfographic.title}
                      </h3>
                      {expandedInfographic.description && (
                        <p style={{ margin: 0, color: '#666', fontSize: 14, lineHeight: 1.5 }}>
                          {expandedInfographic.description}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Column: All Statistics Cards */}
          <div className="stats-grid" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 16 }}>
            {/* Top Statistics */}
            <div style={{ background: '#fff', borderRadius: 12, padding: '20px 24px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <div style={{ fontSize: 16, color: '#666', marginBottom: 8 }}>Total Reports</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontSize: 32, fontWeight: 'bold' }}>{totalReports}</div>
                <span style={{ fontSize: 28, color: '#3f51b5' }}><FaChartSimple /></span>
              </div>
            </div>
            <div style={{ background: '#fff', borderRadius: 12, padding: '20px 24px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <div style={{ fontSize: 16, color: '#666', marginBottom: 8 }}>Reports Active</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontSize: 32, fontWeight: 'bold' }}>{reportsActive}</div>
                <span style={{ fontSize: 28, color: '#28a745' }}><FaFileCircleExclamation /></span>
              </div>
            </div>
            <div style={{ background: '#fff', borderRadius: 12, padding: '20px 24px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <div style={{ fontSize: 16, color: '#666', marginBottom: 8 }}>Occupied Evacuation Centers</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontSize: 32, fontWeight: 'bold' }}>{occupiedEvacuationCenters}</div>
                <span style={{ fontSize: 28, color: '#e74c3c' }}><FaHouseUser /></span>
              </div>
            </div>
            <div style={{ background: '#fff', borderRadius: 12, padding: '20px 24px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <div style={{ fontSize: 16, color: '#666', marginBottom: 8 }}>Total Evacuation Centers</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontSize: 32, fontWeight: 'bold' }}>{numberOfEvacuationCenters}</div>
                <span style={{ fontSize: 28, color: '#9b59b6' }}><FaHouse /></span>
              </div>
            </div>
            <div style={{ background: '#fff', borderRadius: 12, padding: '20px 24px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <div style={{ fontSize: 16, color: '#666', marginBottom: 8 }}>Active Temporary Shelters</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontSize: 32, fontWeight: 'bold' }}>{activeTemporaryShelters}</div>
                <span style={{ fontSize: 28, color: '#03a9f4' }}><FaTent /></span>
              </div>
            </div>
            <div style={{ background: '#fff', borderRadius: 12, padding: '20px 24px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <div style={{ fontSize: 16, color: '#666', marginBottom: 8 }}>Pending Temporary Shelters</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontSize: 32, fontWeight: 'bold' }}>{pendingTemporaryShelters}</div>
                <span style={{ fontSize: 28, color: '#ffc107' }}><FaClock /></span>
              </div>
            </div>
            <div style={{ background: '#fff', borderRadius: 12, padding: '20px 24px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <div style={{ fontSize: 16, color: '#666', marginBottom: 8 }}>Available Space for Evacuees</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontSize: 32, fontWeight: 'bold' }}>{availableEvacuationCenters}</div>
                <span style={{ fontSize: 28, color: '#2ecc71' }}><FaCheck /></span>
              </div>
            </div>
            <div style={{ background: '#fff', borderRadius: 12, padding: '20px 24px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <div style={{ fontSize: 16, color: '#666', marginBottom: 8 }}>Total Evacuees</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontSize: 32, fontWeight: 'bold' }}>{numberOfEvacuees}</div>
                <span style={{ fontSize: 28, color: '#f39c12' }}><FaUserGroup /></span>
              </div>
            </div>
          </div>
        </div>

        {/* Upload Infographic Modal */}
        {showUploadModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
          }}
            onClick={closeUploadModal}
          >
            <div style={{ 
              position: 'relative', 
              background: '#fff', 
              borderRadius: 12, 
              padding: 24, 
              boxShadow: '0 4px 24px #0005', 
              maxWidth: '500px', 
              width: '90vw',
              maxHeight: '90vh',
              overflow: 'auto'
            }} onClick={e => e.stopPropagation()}>
              <button 
                onClick={closeUploadModal} 
                style={{ 
                  position: 'absolute', 
                  top: 16, 
                  right: 16, 
                  background: 'transparent', 
                  border: 'none', 
                  fontSize: 24, 
                  cursor: 'pointer', 
                  color: '#333' 
                }}
              >
                &times;
              </button>
              
              <div style={{ marginBottom: 24 }}>
                <h2 style={{ margin: '0 0 16px 0', fontSize: 24, fontWeight: 'bold' }}>Upload New Infographic</h2>
                <p style={{ margin: 0, color: '#666', fontSize: 14 }}>
                  Upload safety tips, emergency procedures, or other informative graphics.
                </p>
              </div>

              <div style={{ marginBottom: 24 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>
                  Title: <span style={{ color: '#e74c3c' }}>*</span>
                </label>
                <input
                  type="text"
                  value={infographicTitle}
                  onChange={(e) => setInfographicTitle(e.target.value)}
                  placeholder="Enter a clear, descriptive title for mobile users"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: 8,
                    fontSize: 14,
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ marginBottom: 24 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>
                  Select Image File: <span style={{ color: '#e74c3c' }}>*</span>
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px dashed #ddd',
                    borderRadius: 8,
                    background: '#f9f9f9',
                    cursor: 'pointer'
                  }}
                />
                {selectedFile && (
                  <div style={{ marginTop: 12, padding: 12, background: '#e8f5e8', borderRadius: 8, fontSize: 14 }}>
                    <strong>Selected file:</strong> {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button
                  onClick={closeUploadModal}
                  style={{
                    padding: '12px 24px',
                    border: '1px solid #ddd',
                    borderRadius: 8,
                    background: 'white',
                    cursor: 'pointer',
                    fontSize: 14
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={uploadInfographic}
                  disabled={!selectedFile || !infographicTitle.trim() || uploadingInfographic}
                  style={{
                    padding: '12px 24px',
                    border: 'none',
                    borderRadius: 8,
                    background: selectedFile && infographicTitle.trim() && !uploadingInfographic ? '#3f51b5' : '#ccc',
                    color: 'white',
                    cursor: selectedFile && infographicTitle.trim() && !uploadingInfographic ? 'pointer' : 'not-allowed',
                    fontSize: 14,
                    fontWeight: 'bold'
                  }}
                >
                  {uploadingInfographic ? 'Uploading...' : 'Upload Infographic'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Dashboard;